/**
 * Facade de locais do chat: distância, endereço e escolha entre Google e Nominatim.
 */
import {
    hasGooglePlacesSearch,
    reverseGeocodeGoogle,
    searchGoogleNearbyPlaces,
    searchGooglePlaces,
} from './googlePlacesSearch';
import { reverseGeocodeOsm, searchOsmNearby, searchOsmText } from './nominatimPlacesSearch';

export interface PlaceResult {
    id: string;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
}

function haversineKm(
    a: { latitude: number; longitude: number },
    b: { latitude: number; longitude: number }
): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(b.latitude - a.latitude);
    const dLon = toRad(b.longitude - a.longitude);
    const lat1 = toRad(a.latitude);
    const lat2 = toRad(b.latitude);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(h));
}

function formatDistance(km: number): string {
    if (km < 1) return `${Math.max(1, Math.round(km * 1000))} m`;
    return `${km.toFixed(1).replace('.', ',')} km`;
}

function baseAddress(address: string): string {
    return address.includes(' · ') ? address.split(' · ')[0] : address;
}

function labeledAddress(address: string, km: number): string {
    return `${baseAddress(address)} · ${formatDistance(km)}`;
}

function withDistanceLabel(
    places: PlaceResult[],
    near?: { latitude: number; longitude: number } | null,
    limit = 15
): PlaceResult[] {
    const list = places.slice(0, limit);
    if (!near) return list;
    return list.map((place) => ({
        ...place,
        address: labeledAddress(place.address, haversineKm(near, place)),
    }));
}

function placeKey(place: PlaceResult): string {
    return `${place.name.toLowerCase()}|${place.latitude.toFixed(4)}|${place.longitude.toFixed(4)}`;
}

/** Google primeiro. Chave inválida (400 / REQUEST_DENIED) cai no Nominatim. */
async function preferGoogle<T>(google: () => Promise<T>, osm: () => Promise<T>): Promise<T> {
    if (!hasGooglePlacesSearch()) return osm();
    try {
        return await google();
    } catch {
        return osm();
    }
}

/** Locais próximos via Places Nearby Search. */
export async function searchNearbyPlaces(near: {
    latitude: number;
    longitude: number;
}): Promise<PlaceResult[]> {
    const places = await preferGoogle(
        () => searchGoogleNearbyPlaces(near),
        () => searchOsmNearby(near),
    );
    const seen = new Set<string>();
    const withKm = places
        .map((place) => ({
            ...place,
            distanceKm: haversineKm(near, place),
        }))
        .filter((place) => place.distanceKm <= 2.2)
        .sort((a, b) => a.distanceKm - b.distanceKm);

    return withKm
        .filter((place) => {
            const key = placeKey(place);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .slice(0, 20)
        .map(({ distanceKm, ...place }) => ({
            ...place,
            address: labeledAddress(place.address, distanceKm),
        }));
}

/** Busca textual nacional via Places Text Search. */
export async function searchPlaces(
    query: string,
    near?: { latitude: number; longitude: number } | null
): Promise<PlaceResult[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    const places = await preferGoogle(() => searchGooglePlaces(q), () => searchOsmText(q));
    return withDistanceLabel(places, near, 15);
}

export async function reverseGeocode(
    latitude: number,
    longitude: number
): Promise<PlaceResult> {
    return preferGoogle(
        () => reverseGeocodeGoogle(latitude, longitude),
        () => reverseGeocodeOsm(latitude, longitude),
    );
}

/** Remove sufixo de distância do endereço antes de enviar/exibir. */
export function stripDistanceSuffix(address: string): string {
    return address.replace(/\s*·\s*\d+([\.,]\d+)?\s*(m|km)\s*$/i, '').trim();
}

/** Endereço do local sem o sufixo de distância. */
export function cleanPlaceAddress(place: { name?: string; address?: string }): string {
    return stripDistanceSuffix(place.address || place.name || '');
}

/** True se o texto parece só coordenada (sem endereço). */
export function isCoordinateOnlyText(value: string | undefined | null): boolean {
    if (!value) return true;
    return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(value.trim());
}
